import type { User } from '@core/types/auth';

/**
 * Session persistence for auth.
 *
 * Keeps the EXACT localStorage keys the Angular app used (`jwt_token`, `user_data`,
 * `otp_detail`) for two reasons:
 *   1. Deploying the React build over the Angular one does not log everyone out.
 *   2. During the migration you can run :4200 and :5173 against the same backend
 *      and compare screens without re-authenticating in each.
 *
 * "Remember Me" — a checkbox that existed in the Angular login template with no
 * `formControlName` and therefore did nothing — is now real: checked persists to
 * localStorage (survives browser restart), unchecked persists to sessionStorage
 * (dies with the tab). A marker key records which store owns the current session so
 * reads go to the right place after a reload.
 */

const TOKEN_KEY = 'jwt_token';
const USER_KEY = 'user_data';
const OTP_DETAIL_KEY = 'otp_detail';
/** Records which Storage holds the active session. localStorage-only by design. */
const PERSISTENCE_KEY = 'vm_auth_persistence';

export type AuthPersistence = 'local' | 'session';

function safeStorage(kind: AuthPersistence): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    // Private-browsing modes can throw on storage access.
    return null;
  }
}

function readPersistence(): AuthPersistence {
  try {
    return window.localStorage.getItem(PERSISTENCE_KEY) === 'session' ? 'session' : 'local';
  } catch {
    return 'local';
  }
}

function activeStorage(): Storage | null {
  return safeStorage(readPersistence());
}

/** Reads a key from whichever store owns the session, falling back to the other. */
function readEither(key: string): string | null {
  const primary = activeStorage()?.getItem(key) ?? null;
  if (primary !== null) return primary;
  const other = readPersistence() === 'local' ? 'session' : 'local';
  return safeStorage(other)?.getItem(key) ?? null;
}

export const authStorage = {
  getToken(): string | null {
    return readEither(TOKEN_KEY);
  },

  getUser(): User | null {
    const raw = readEither(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as User;
    } catch {
      // Angular logged and returned null here (auth.service.ts:267-269).
      console.error('Error parsing user data from storage');
      return null;
    }
  },

  getOtpDetail(): string | null {
    return readEither(OTP_DETAIL_KEY);
  },

  setSession(token: string, user: User, persistence: AuthPersistence): void {
    // Clear the other store first so a stale token cannot shadow the new one.
    const other = persistence === 'local' ? 'session' : 'local';
    const otherStore = safeStorage(other);
    otherStore?.removeItem(TOKEN_KEY);
    otherStore?.removeItem(USER_KEY);

    try {
      window.localStorage.setItem(PERSISTENCE_KEY, persistence);
    } catch {
      /* storage unavailable — session degrades to in-memory only */
    }

    const store = safeStorage(persistence);
    store?.setItem(TOKEN_KEY, token);
    store?.setItem(USER_KEY, JSON.stringify(user));
  },

  /** Replaces the stored user without disturbing the token or persistence mode. */
  setUser(user: User): void {
    activeStorage()?.setItem(USER_KEY, JSON.stringify(user));
  },

  /** `validateOTP` overwrites `jwt_token` with the OTP-scoped token. */
  setToken(token: string): void {
    activeStorage()?.setItem(TOKEN_KEY, token);
  },

  setOtpDetail(otpDetail: string): void {
    activeStorage()?.setItem(OTP_DETAIL_KEY, otpDetail);
  },

  clearOtpDetail(): void {
    safeStorage('local')?.removeItem(OTP_DETAIL_KEY);
    safeStorage('session')?.removeItem(OTP_DETAIL_KEY);
  },

  /** Clears both stores — never leave a token behind on logout. */
  clear(): void {
    for (const kind of ['local', 'session'] as const) {
      const store = safeStorage(kind);
      store?.removeItem(TOKEN_KEY);
      store?.removeItem(USER_KEY);
      store?.removeItem(OTP_DETAIL_KEY);
    }
    try {
      window.localStorage.removeItem(PERSISTENCE_KEY);
    } catch {
      /* nothing to clean up */
    }
  },
} as const;
